import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Menu,
  MenuList,
  MenuItem as MenuItemComponent,
  ListItemIcon,
  ListItemText,
  Toolbar,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Publish as PublishIcon,
  Archive as ArchiveIcon,
  Edit as DraftIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../../../../contexts/AuthContext';
import { 
  getPages, 
  deletePage, 
  updatePage, 
  CMSPage,
  validatePageData 
} from '../../../../firebase/services/cmsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pages-tabpanel-${index}`}
      aria-labelledby={`pages-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const PagesPage: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState<CMSPage[]>([]);
  const [filteredPages, setFilteredPages] = useState<CMSPage[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft' | 'archived'>('all');
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedPage, setSelectedPage] = useState<CMSPage | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPages();
  }, []);

  useEffect(() => {
    filterPages();
  }, [pages, searchTerm, statusFilter, tabValue]);

  const loadPages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { pages: allPages } = await getPages();
      setPages(allPages);
      
    } catch (err: any) {
      console.error('Error loading pages:', err);
      setError(err.message || 'Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const filterPages = () => {
    let filtered = pages;

    // Filter by status based on tab
    if (tabValue === 1) filtered = filtered.filter(p => p.status === 'published');
    else if (tabValue === 2) filtered = filtered.filter(p => p.status === 'draft');
    else if (tabValue === 3) filtered = filtered.filter(p => p.status === 'archived');

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(page => 
        page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        page.slug.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPages(filtered);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (pageToDelete: CMSPage) => {
    setSelectedPage(pageToDelete);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPage) return;

    try {
      await deletePage(selectedPage.id);
      setPages(prev => prev.filter(p => p.id !== selectedPage.id));
      setDeleteDialogOpen(false);
      setSelectedPage(null);
    } catch (err: any) {
      console.error('Error deleting page:', err);
      setError(err.message || 'Failed to delete page');
    }
  };

  const handleStatusChange = async (pageToUpdate: CMSPage, newStatus: 'published' | 'draft' | 'archived') => {
    try {
      const updates: Partial<CMSPage> = {
        status: newStatus,
        updatedBy: userProfile?.uid || 'unknown',
      };

      if (newStatus === 'published') {
        updates.publishedAt = Date.now();
      }

      await updatePage(pageToUpdate.id, updates);
      
      setPages(prev => prev.map(p => 
        p.id === pageToUpdate.id 
          ? { ...p, ...updates, updatedAt: Date.now() }
          : p
      ));
      
      handleMenuClose();
    } catch (err: any) {
      console.error('Error updating page status:', err);
      setError(err.message || 'Failed to update page status');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, page: CMSPage) => {
    setMenuAnchor(event.currentTarget);
    setSelectedPage(page);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedPage(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'published':
        return <PublishIcon fontSize="small" />;
      case 'draft':
        return <DraftIcon fontSize="small" />;
      case 'archived':
        return <ArchiveIcon fontSize="small" />;
      default:
        return <DraftIcon fontSize="small" />;
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTabCounts = () => {
    return {
      all: pages.length,
      published: pages.filter(p => p.status === 'published').length,
      draft: pages.filter(p => p.status === 'draft').length,
      archived: pages.filter(p => p.status === 'archived').length,
    };
  };

  const tabCounts = getTabCounts();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Pages
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your website pages and content
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={Link}
            to="/admin/business/online/pages/new"
          >
            Create New Page
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Search and Filters */}
        <Paper sx={{ mb: 3 }}>
          <Toolbar sx={{ px: 2 }}>
            <TextField
              placeholder="Search pages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Toolbar>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="page status tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`All (${tabCounts.all})`} />
            <Tab label={`Published (${tabCounts.published})`} />
            <Tab label={`Drafts (${tabCounts.draft})`} />
            <Tab label={`Archived (${tabCounts.archived})`} />
          </Tabs>

          {/* Pages Table */}
          <TabPanel value={tabValue} index={tabValue}>
            {filteredPages.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="textSecondary" gutterBottom>
                  {searchTerm 
                    ? 'No pages found matching your search'
                    : tabValue === 0 
                      ? 'No pages created yet'
                      : `No ${['', 'published', 'draft', 'archived'][tabValue]} pages`
                  }
                </Typography>
                {tabValue === 0 && !searchTerm && (
                  <Button
                    variant="contained"
                    component={Link}
                    to="/admin/business/online/pages/new"
                    sx={{ mt: 2 }}
                    startIcon={<AddIcon />}
                  >
                    Create Your First Page
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Slug</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Last Modified</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPages
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((pageItem) => (
                          <TableRow key={pageItem.id} hover>
                            <TableCell>
                              <Typography variant="subtitle2">
                                {pageItem.title}
                              </Typography>
                              {pageItem.metaDescription && (
                                <Typography variant="caption" color="text.secondary">
                                  {pageItem.metaDescription.substring(0, 100)}
                                  {pageItem.metaDescription.length > 100 ? '...' : ''}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                /{pageItem.slug}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={pageItem.status}
                                color={getStatusColor(pageItem.status) as any}
                                size="small"
                                icon={getStatusIcon(pageItem.status)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {pageItem.template}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(pageItem.updatedAt)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                component={Link}
                                to={`/admin/business/online/pages/${pageItem.id}/edit`}
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                              {pageItem.status === 'published' && (
                                <IconButton
                                  href={`/${pageItem.slug}`}
                                  target="_blank"
                                  size="small"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              )}
                              <IconButton
                                onClick={(e) => handleMenuOpen(e, pageItem)}
                                size="small"
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredPages.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </TabPanel>
        </Paper>

        {/* Action Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          {selectedPage && selectedPage.status !== 'published' && (
            <MenuItemComponent onClick={() => handleStatusChange(selectedPage, 'published')}>
              <ListItemIcon>
                <PublishIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Publish" />
            </MenuItemComponent>
          )}
          {selectedPage && selectedPage.status !== 'draft' && (
            <MenuItemComponent onClick={() => handleStatusChange(selectedPage, 'draft')}>
              <ListItemIcon>
                <DraftIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Move to Draft" />
            </MenuItemComponent>
          )}
          {selectedPage && selectedPage.status !== 'archived' && (
            <MenuItemComponent onClick={() => handleStatusChange(selectedPage, 'archived')}>
              <ListItemIcon>
                <ArchiveIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Archive" />
            </MenuItemComponent>
          )}
          <MenuItemComponent onClick={() => selectedPage && handleDeleteClick(selectedPage)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItemComponent>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Page</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the page "{selectedPage?.title}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default PagesPage; 